import { unrefElement, useElementBounding, useWindowSize } from '@vueuse/core';
import { getWalkthroughTarget } from '@/composables/useWalkthroughTarget';

/**
 * Walkthrough Controller: a single, app-wide observer — module-level state,
 * mirroring the target registry's singleton pattern (useWalkthroughTarget.js),
 * not a fresh instance per caller. Every caller shares the same running
 * walkthrough: one component (mounted once) renders the actual Tutorial
 * instance against `tutorialStyle`; everywhere else (a modal card's CTA, a
 * What's New panel item) just calls start()/advance()/cancel() without
 * rendering anything itself.
 *
 * `settleActiveIndex` is the single place that both advances to a step and
 * skips it if its target isn't currently *visible* (see `isStepTargetVisible`
 * — mounted is not enough) — it does not auto-navigate or wait for a target to
 * appear, it just moves forward until it finds a visible target or runs out of
 * steps, which ends the walkthrough. A watcher at the bottom of this file
 * routes "the active step's target just vanished" through the same function.
 * `cancel()` is a distinct, user-initiated exit path, added separately so it
 * can't be conflated with this skip behavior.
 *
 * Steps never span pages: a walkthrough declares one `route`, the launcher
 * navigates there before starting, and everything not visible at that point is
 * skipped. Getting the page into the right shape first (opening a sidebar, say)
 * is the job of the walkthrough's `setup`, not of this controller.
 */
const steps = ref([]);
const activeIndex = ref(-1);

const isRunning = computed(() => activeIndex.value >= 0);
const totalSteps = computed(() => steps.value.length);
const activeStep = computed(() => steps.value[activeIndex.value] ?? null);

const activeTargetElement = computed(() =>
  activeStep.value ? getWalkthroughTarget(activeStep.value.stepId).value : null,
);

const { top, left, right, bottom, width, height, update } = useElementBounding(activeTargetElement);
const { width: viewportWidth, height: viewportHeight } = useWindowSize();

/**
 * The rendered tooltip, handed over by whichever component renders it (Walkthrough/
 * Controller.vue). Its size is needed to keep it on screen: without measuring it, the only
 * option is to centre it on the target and hope, which clips whenever a target sits near a
 * viewport edge.
 */
const tutorialElement = ref(null);

const {
  width: tutorialWidth,
  height: tutorialHeight,
  update: updateTutorialBounding,
} = useElementBounding(tutorialElement);

const setTutorialElement = (element) => {
  tutorialElement.value = element ?? null;
  updateTutorialBounding();
};

/**
 * Re-measures both boxes. Consumers call this after anything that could move either — the
 * same `update` the old single-element version exposed, except the tooltip's own size now
 * feeds positioning too, so measuring only the target would leave clamping working from a
 * stale tooltip size.
 */
const recalculatePosition = () => {
  update();
  updateTutorialBounding();
};

// Gap between the tooltip and the target it's pointing at.
const PLACEMENT_GAP = 8;

// Smallest gap left between the tooltip and the viewport edge when clamping.
const VIEWPORT_MARGIN = 8;

/**
 * How close the arrow may sit to a tooltip corner. Keeps it clear of the rounded corner
 * once clamping has slid the tooltip away from the target's centre.
 */
const ARROW_EDGE_INSET = 20;

const clamp = (value, min, max) => Math.min(Math.max(value, min), Math.max(min, max));

/**
 * Which side of the target the tooltip renders on, chosen automatically from
 * whichever side has the most available space so a target near a viewport edge
 * never gets a tooltip rendered off-screen. Pure
 * function of the target's rect and the viewport size (both already reactive
 * via useElementBounding/useWindowSize), so it re-evaluates on scroll/resize
 * for free and is testable with fake bounding-rect values, no real layout needed.
 */
const placement = computed(() => {
  const spaceAbove = top.value;
  const spaceBelow = viewportHeight.value - bottom.value;
  const spaceLeft = left.value;
  const spaceRight = viewportWidth.value - right.value;

  const verticalSpace = Math.max(spaceAbove, spaceBelow);
  const horizontalSpace = Math.max(spaceLeft, spaceRight);

  if (verticalSpace >= horizontalSpace) {
    return spaceBelow >= spaceAbove ? 'below' : 'above';
  }
  return spaceRight >= spaceLeft ? 'right' : 'left';
});

// The arrow renders on whichever tooltip edge faces the target — the
// opposite side from wherever the tooltip itself was placed.
const arrowDirection = computed(
  () =>
    ({
      above: 'bottom',
      below: 'top',
      left: 'right',
      right: 'left',
    })[placement.value],
);

/**
 * The tooltip's top-left corner, in viewport coordinates, before clamping: centred on the
 * target along the cross axis and offset by `PLACEMENT_GAP` along the placement axis.
 *
 * Concrete coordinates rather than CSS transforms (`translate(-50%, -50%)` and friends,
 * which this used to rely on) because a transform can't be clamped — the browser applies it
 * after any positioning, so the box lands wherever it lands, half off-screen if the target
 * is near an edge. Computing the corner ourselves makes the box's extent knowable, and
 * therefore correctable.
 */
const unclampedPosition = computed(() => {
  const targetMidX = left.value + width.value / 2;
  const targetMidY = top.value + height.value / 2;

  switch (placement.value) {
    case 'below':
      return { x: targetMidX - tutorialWidth.value / 2, y: bottom.value + PLACEMENT_GAP };
    case 'left':
      return {
        x: left.value - PLACEMENT_GAP - tutorialWidth.value,
        y: targetMidY - tutorialHeight.value / 2,
      };
    case 'right':
      return { x: right.value + PLACEMENT_GAP, y: targetMidY - tutorialHeight.value / 2 };
    case 'above':
    default:
      return {
        x: targetMidX - tutorialWidth.value / 2,
        y: top.value - PLACEMENT_GAP - tutorialHeight.value,
      };
  }
});

/**
 * The same corner, pulled back inside the viewport. `placement` already picks the side with
 * the most room, but that only decides *which* side — a tooltip taller or wider than the
 * space alongside its target still overhangs, which is what clipped the last step of the
 * Saved Searches walkthrough against the bottom of the window.
 */
const clampedPosition = computed(() => {
  const { x, y } = unclampedPosition.value;

  return {
    x: clamp(x, VIEWPORT_MARGIN, viewportWidth.value - tutorialWidth.value - VIEWPORT_MARGIN),
    y: clamp(y, VIEWPORT_MARGIN, viewportHeight.value - tutorialHeight.value - VIEWPORT_MARGIN),
  };
});

/**
 * Deliberately never hides the tooltip. An earlier version set `visibility: hidden` until the
 * tooltip had been measured, to avoid a one-frame jump — but that makes an unmeasured tooltip
 * permanently invisible, which is indistinguishable from the walkthrough being broken. A
 * tooltip briefly a few pixels off is a far better failure than one nobody can see, so with a
 * zero size the clamp simply keeps it inside the viewport and it settles once measured.
 */
const tutorialStyle = computed(() => {
  const { x, y } = clampedPosition.value;

  return {
    position: 'fixed',
    'z-index': 200,
    top: `${y}px`,
    left: `${x}px`,
  };
});

/**
 * Where the arrow sits along the tooltip edge facing the target, as a CSS length from that
 * edge's start. Needed because clamping decouples the tooltip's centre from the target's:
 * the arrow is pinned to the middle of its edge by default, so a clamped tooltip would point
 * at empty space. Kept `ARROW_EDGE_INSET` clear of both corners so it never straddles the
 * rounded edge; when nothing is clamped this lands exactly at the midpoint anyway.
 */
const arrowOffset = computed(() => {
  const isVertical = placement.value === 'above' || placement.value === 'below';
  const { x, y } = clampedPosition.value;

  if (isVertical) {
    const targetMidX = left.value + width.value / 2;
    const span = tutorialWidth.value;
    return `${clamp(targetMidX - x, ARROW_EDGE_INSET, span - ARROW_EDGE_INSET)}px`;
  }

  const targetMidY = top.value + height.value / 2;
  const span = tutorialHeight.value;
  return `${clamp(targetMidY - y, ARROW_EDGE_INSET, span - ARROW_EDGE_INSET)}px`;
});

// Tutorial.vue is always told `total-steps="1"`, so its own internal label
// logic can't be used — the Controller owns the real step count and, with
// it, the "Next" vs "Got it!" label a consuming component renders itself
// (e.g. via Tutorial's `tutorial-footer` slot).
const nextButtonLabel = computed(() =>
  activeIndex.value + 1 < totalSteps.value ? 'Next' : 'Got it!',
);

// Whether there's any earlier step to go back to — a consuming component
// uses this to decide whether to render a "Previous" control at all.
const isFirstStep = computed(() => activeIndex.value <= 0);

/** The registry stores component instances as readily as elements; measuring needs the element. */
const resolveStepElement = (index) => {
  const step = steps.value[index];
  if (!step) {
    return null;
  }
  return unrefElement(getWalkthroughTarget(step.stepId));
};

/**
 * Is this element actually visible to the carrier — not merely mounted? Registration only
 * proves a component rendered; it says nothing about a collapsed container, a
 * `display: none` parent, or (the case that prompted this) an element sitting underneath a
 * full-screen overlay. Pointing a tooltip at something invisible is worse than skipping the
 * step, so both are checked:
 *
 * 1. a non-zero bounding rect — catches display:none, zero-size and collapsed containers
 * 2. a hit test at the element's centre — catches anything painted over the top of it
 *
 * An **ancestor** winning the hit test counts as visible, not covered. `elementFromPoint`
 * honours `pointer-events`, so a target that opts out of hit-testing (the Saved Searches
 * button does, once it gains its `active` class) reports its containing wrapper instead —
 * which proves the point lands inside the target's own subtree, i.e. nothing foreign is on
 * top. A genuine overlay, like a modal scrim or a slid-in panel, lives in a different
 * subtree and is neither an ancestor nor a descendant, so it still fails.
 */
const isElementVisible = (element) => {
  if (!element?.getBoundingClientRect) {
    return false;
  }

  const rect = element.getBoundingClientRect();
  if (!rect.width || !rect.height) {
    return false;
  }

  const x = rect.left + rect.width / 2;
  const y = rect.top + rect.height / 2;

  // Skip the tooltip itself (it can clamp on top of its own target) to find what's really on top.
  const stack = document.elementsFromPoint?.(x, y) ?? [document.elementFromPoint(x, y)];
  const topmost = stack.find((candidate) => !tutorialElement.value?.contains(candidate)) ?? null;

  if (!topmost) {
    // Outside the viewport entirely — the caller decides whether scrolling can help.
    return false;
  }

  return element === topmost || element.contains(topmost) || topmost.contains(element);
};

/**
 * Test, scroll, test — in that order, so an already-visible target is never scrolled to
 * gratuitously. Only when the first test fails is the element brought into view and
 * re-tested; a second failure means it's genuinely unreachable (covered, collapsed) and
 * scrolling can't fix that, so the step is skipped.
 */
const isStepTargetVisible = (index) => {
  const element = resolveStepElement(index);
  if (!element) {
    return false;
  }

  if (isElementVisible(element)) {
    return true;
  }

  element.scrollIntoView?.({ block: 'center' });
  return isElementVisible(element);
};

/**
 * Is the target for this step id visible right now? Same rule the Controller applies when
 * choosing a step, minus the scroll — a caller polling this wants to observe the page
 * settling, not nudge it. The launcher uses it to wait for the modal it just closed to
 * actually leave the DOM before starting, since a covering scrim would otherwise make every
 * step fail its check and the walkthrough would end the instant it began.
 */
export const isWalkthroughTargetVisible = (stepId) =>
  isElementVisible(unrefElement(getWalkthroughTarget(stepId)));

/**
 * While a walkthrough runs, keep checking that the step being shown is still visible, and
 * settle off it the moment it isn't.
 *
 * The unmount watcher below can't cover this: the containers a walkthrough points into
 * usually stay mounted and merely hide. Close the Saved Searches panel mid-walkthrough and
 * its buttons are still registered, still measurable — just slid off-screen — so without
 * this the tooltip would hang around pointing at a panel that isn't there any more.
 *
 * Polled rather than watched because visibility isn't reactive: neither a CSS transform nor
 * something painting over the target notifies anyone. The interval only runs for the
 * lifetime of a walkthrough, which is seconds, and deliberately uses the plain visibility
 * test — a poll must not scroll the page underneath the carrier.
 */
const VISIBILITY_WATCH_MS = 300;
let visibilityWatchTimer = null;

const stopVisibilityWatch = () => {
  if (visibilityWatchTimer) {
    clearInterval(visibilityWatchTimer);
    visibilityWatchTimer = null;
  }
};

const startVisibilityWatch = () => {
  stopVisibilityWatch();

  if (typeof window === 'undefined') {
    return;
  }

  visibilityWatchTimer = setInterval(() => {
    if (!isRunning.value) {
      stopVisibilityWatch();
      return;
    }

    if (!isElementVisible(resolveStepElement(activeIndex.value))) {
      // Same treatment as a target that unmounted: move to the next visible step, or end.
      settleActiveIndex(activeIndex.value + 1);
    }
  }, VISIBILITY_WATCH_MS);
};

// Set by start() for the run currently in progress; fired at most once each,
// then cleared, so a run's callbacks can never fire twice or leak into the
// next run. Story 8 (analytics) hangs its trackEvent() calls off these —
// this composable stays ignorant of announcement_id/trackEvent itself,
// since the caller of start() already has that context.
let onCompleteCallback = null;
let onCancelCallback = null;

const clearLifecycleCallbacks = () => {
  onCompleteCallback = null;
  onCancelCallback = null;
};

// The single place that both advances to a step and skips it if its target
// isn't currently mounted. Running out of steps this way ends the
// walkthrough and counts as "completed" — whether that's because advance()
// reached the true last step, or because every remaining step's target
// disappeared — there's no separate "stuck" event, per the Design Doc's
// Analytics table. Only cancel() below is a distinct, user-initiated exit.
const settleActiveIndex = (index) => {
  const wasRunning = isRunning.value;
  let nextIndex = index;
  while (nextIndex < steps.value.length && !isStepTargetVisible(nextIndex)) {
    nextIndex += 1;
  }

  if (nextIndex < steps.value.length) {
    activeIndex.value = nextIndex;
    return;
  }

  const finalStepIndex = activeIndex.value;
  const finishedTotalSteps = totalSteps.value;
  activeIndex.value = -1;
  stopVisibilityWatch();

  if (wasRunning) {
    onCompleteCallback?.({ finalStepIndex, totalSteps: finishedTotalSteps });
  }
  clearLifecycleCallbacks();
};

const start = (walkthroughSteps, { onComplete, onCancel } = {}) => {
  steps.value = walkthroughSteps;
  onCompleteCallback = onComplete ?? null;
  onCancelCallback = onCancel ?? null;
  settleActiveIndex(0);

  if (isRunning.value) {
    startVisibilityWatch();
  }
};

const advance = () => {
  if (!isRunning.value) return;
  settleActiveIndex(activeIndex.value + 1);
};

const back = () => {
  if (!isRunning.value) return;

  let previousIndex = activeIndex.value - 1;
  while (previousIndex >= 0 && !isStepTargetVisible(previousIndex)) {
    previousIndex -= 1;
  }

  if (previousIndex >= 0) {
    activeIndex.value = previousIndex;
  }
};

// Distinct from settleActiveIndex's skip-to-end: a user-initiated exit,
// never conflated with a step being auto-skipped or the walkthrough running
// its natural course to the end.
const cancel = () => {
  if (!isRunning.value) return;

  const finalStepIndex = activeIndex.value;
  const finishedTotalSteps = totalSteps.value;
  activeIndex.value = -1;
  stopVisibilityWatch();

  onCancelCallback?.({ finalStepIndex, totalSteps: finishedTotalSteps });
  clearLifecycleCallbacks();
};

/**
 * The active step's target can disappear from under a running walkthrough — the carrier
 * navigates away, or closes the panel a `setup` opened. Without this the tooltip kept
 * rendering at the last measured position, pointing at nothing, with no way out.
 *
 * Settling forward from the current index re-runs the ordinary skip logic: the next
 * visible step becomes active, or the walkthrough ends if none remain. That doubles as the
 * carrier's escape hatch, since navigating away unmounts every target on the page.
 *
 * `flush: 'sync'` is required, not stylistic. This watcher is created at module scope, with
 * no owning component instance, and Vue's pre/post queues for an instance-less watcher are
 * never flushed — verified both ways: with the default `pre` (and with `post`) the callback
 * simply never runs, however many ticks you wait.
 */
watch(
  activeTargetElement,
  (element) => {
    if (isRunning.value && !element) {
      settleActiveIndex(activeIndex.value + 1);
    }
  },
  { flush: 'sync' },
);

export const useWalkthroughController = () => ({
  activeStep,
  activeIndex,
  totalSteps,
  isRunning,
  tutorialStyle,
  arrowDirection,
  arrowOffset,
  setTutorialElement,
  nextButtonLabel,
  isFirstStep,
  start,
  advance,
  back,
  cancel,
  recalculatePosition,
});
